import { Tag, type TagProps } from "@tine/designsystem-ui-react";
import groupBy from "lodash-es/groupBy";
import includes from "lodash-es/includes";
import map from "lodash-es/map";
import size from "lodash-es/size";
import { observer } from "mobx-react-lite";

import type { Label, LabelColorVariant, LabelPositionVariant, PageType } from "../common/types/productTypes";

const LABEL_COLOR_MAP: Record<LabelColorVariant, [string, string]> = {
   red: ["var(--color-red-700)", "var(--color-red-100)"],
   blue: ["var(--color-blue-900)", "var(--color-blue-50)"],
   green: ["var(--color-green-700)", "var(--color-green-50)"],
   gray: ["var(--color-base-700)", "var(--color-base-50)"],
   violet: ["var(--color-violet-600)", "var(--color-violet-100)"],
   yellow: ["var(--color-red-700)", "var(--color-warning-100)"]
} as const;

const DEFAULT_COLORS: [string, string] = LABEL_COLOR_MAP.gray;

const mapLabelColor = (label: Label): [string, string] => {
   return LABEL_COLOR_MAP[label.color as LabelColorVariant] ?? DEFAULT_COLORS;
};

type LabelComponentProps = {
   label: Label;
   className?: string;
   size?: TagProps["size"];
};

export const LabelComponent = ({ label, size, className }: LabelComponentProps) => {
   const [textColor, bgColor] = mapLabelColor(label);

   return (
      <Tag variant="base" size={size} className={className} textColor={textColor} bgColor={bgColor}>
         {label.text}
      </Tag>
   );
};

interface LabelProps {
   labels: Label[];
   page: PageType;
   className?: string;
}

const positionStyles: { [position in LabelPositionVariant]: string } = {
   "top-left": "left-0 top-0",
   "top-right": "right-0 top-0",
   "bottom-left": "left-0 bottom-2",
   "bottom-right": "right-0 bottom-2"
};

const Labels = ({ labels, page, className = "" }: LabelProps) => {
   const relevantLabels = groupBy(
      labels.filter((l) => includes(l.page, page)),
      "position"
   );

   if (size(relevantLabels) === 0) {
      return null;
   }

   return (
      <>
         {map(relevantLabels, (labels: Label[], position: LabelPositionVariant) => (
            <div key={position} className={`absolute flex flex-col ${positionStyles[position]} ${className}`}>
               {labels.map((label, index) => (
                  <LabelComponent key={`${label.text}${index + 1}`} label={label} />
               ))}
            </div>
         ))}
      </>
   );
};

export default observer(Labels);
